<template>
  <div class="main-video-root" @click="click" @dblclick="fullScreen">
    <div class="main-video"
         :class="{'main-video__disabled':cameraDisabled,
                  'main-video__portrait': videoSize.width < videoSize.height}"
    >
      <video
        v-if="currentStream"
        v-show="(currentPublisher && (currentPublisher.video
          || currentPublisher.share)) || (isMyStream && !cameraDisabled)"
        ref="mainVideo" :class="{'my-stream': isMyStream && !captured}"
        preload="metadata" muted playsinline
      />
      <div class="main-video__disable">
        <div v-if="cameraDisabled"
             class="main-video__disable-item main-video__disable-item_cam"
        >
          {{ $t('offCamera') }}
        </div>
        <div v-if="currentPublisher && !currentPublisher.audio && !isMyStream
               || (isMyStream && microphoneDisabled)"
             class="main-video__disable-item main-video__disable-item_mic"
        >
          {{ $t('offMic') }}
        </div>
      </div>
      <div v-show="!currentStream || !((currentPublisher && (currentPublisher.video
             || currentPublisher.share)) || (isMyStream && !cameraDisabled))"
           class="main-video__non-video-container"
      >
        <img class="main-video__non-video-image" src="../assets/img/icons/user.svg" alt="user icon">
        <p v-if="currentPublisher" class="main-video__non-video-name">
          {{ currentPublisher.data.name }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import useStreams from '@/compositions/useStreams';
import { watchEffect, ref, computed } from '@vue/composition-api';
import useVideo from '@/compositions/useVideo';
import useCaptureScreen from '@/compositions/useCaptureScreen';
import useJanusPublisher from '@/compositions/useJanusPublisher';
import useFullScreen from '@/compositions/useFullScreen';

export default {
  name: 'VideoRoomMainVideo',
  components: {},
  setup() {
    const {
      currentStream, isMyStream, currentUserId, cameraDisabled, microphoneDisabled,
    } = useStreams();
    const { captured } = useCaptureScreen();
    const { setVideo } = useVideo();
    const { requestFullScreen, isFullScreen, exitFullScreen } = useFullScreen();
    const { publishers } = useJanusPublisher();
    const mainVideo = ref(null);

    watchEffect(async () => {
      await setVideo(currentStream, mainVideo, {
        mainVideo: true,
      });
    });

    const currentPublisher = computed(
      () => publishers.value.find((p) => p.data?.janusRoomMemberId === currentUserId.value),
    );

    const videoSizeResult = ref({
      width: 0,
      height: 0,
    });

    const videoSizeResultInterval = ref(null);

    videoSizeResultInterval.value = setInterval(() => {
      if (mainVideo.value?.videoHeight) {
        videoSizeResult.value = {
          height: mainVideo.value?.videoHeight,
          width: mainVideo.value?.videoWidth,
        };
        clearInterval(videoSizeResultInterval.value);
      }
    }, 1000);

    const currentPublisherExists = computed(() => !!currentPublisher?.value?.data?.name);

    return {
      currentStream,
      mainVideo,
      isMyStream,
      captured,
      currentUserId,
      publishers,
      microphoneDisabled,
      cameraDisabled: computed(() => (currentPublisher.value && !currentPublisher.value.video
          && !currentPublisher.value.share && (!isMyStream.value)
      ) || (isMyStream.value && cameraDisabled.value)),
      currentPublisher,
      videoSize: computed(() => videoSizeResult.value),
      click: () => {
        const event = new Event('main-video-click');
        document.dispatchEvent(event);
      },
      fullScreen() {
        if (isFullScreen.value) {
          exitFullScreen();
          return;
        }
        requestFullScreen();
      },
      currentPublisherExists,
    };
  },
};
</script>

<style lang="scss" scoped>
.main-video-root {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.my-stream {
  transform: scale(-1, 1);
}

.main-video {
  width: 100%;
  flex: 1;
  min-height: 0;
  background: #1C1C1E;
  position: relative;

  video {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &__disable {
    display: flex;
    gap: 0.375rem;
    position: absolute;
    bottom: 1rem;
    left: 1rem;
    font-size: 0.8125rem;
    color: rgba(255, 255, 255, 0.85);
  }

  &__disable-item {
    display: inline-flex;
    align-items: center;
    border-radius: 100px;
    padding: 0.3rem 0.625rem 0.3rem 1.875rem;
    background: rgba(0, 0, 0, 0.6) no-repeat 0.5rem center;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    background-size: 0.875rem;
    font-size: 0.75rem;
    font-weight: 500;

    &_mic {
      background-image: url("../assets/img/icons/mic-off.svg");
    }

    &_cam {
      background-image: url("../assets/img/icons/cam-off.svg");
    }
  }

  &__non-video {
    &-container {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.875rem;
    }
    &-image {
      width: 8rem;
      height: 8rem;
      border-radius: 50%;
      background: linear-gradient(135deg, #2C2C2E 0%, #3A3A3C 100%);
      border: 2px solid rgba(255, 255, 255, 0.08);
      object-fit: contain;
      padding: 1.5rem;
      opacity: 0.5;
    }
    &-name {
      margin: 0;
      font-size: 0.9375rem;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.75);
      text-align: center;
      white-space: nowrap;
    }
  }
}

@media (max-width: 1199px) {
  .main-video {
      display: flex;
    align-content: center;
    justify-content: center;
    video {
      height: 100%;
    }
  }
}

@media only screen and (orientation: landscape) and (max-width: 959px),
only screen and (orientation: portrait) and (max-width: 959px) {
  .main-video {
    height: 100vh;

    &__disabled {
      background-position: center;
    }

    &__disable-item {
      display: none;
    }
    video {
      object-fit: cover;
      max-height: 200%;
      height: auto;
    }
  }
}

@media only screen and (orientation: landscape) and (max-width: 959px) {
  .main-video {
    video {
      object-fit: cover;
    }
  }
}

</style>

<i18n locale="en" lang="yaml">
offMic: Microphone id disabled
offCamera: Camera id disabled
</i18n>

<i18n locale="ru" lang="yaml">
offMic: Микрофон выключен
offCamera: Камера выключена
</i18n>
